import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  HostListener,
  Inject, ChangeDetectionStrategy} from '@angular/core';
import { Router} from "@angular/router";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { PageService } from '@irb-common/services/page.service';
import { AppService } from '@irb/app.service';
import { IEvent, IEventSubscriber, IrbSettingsEvents, IrbAppEvents } from '@irb-common/events';
import { ReplaySubject, Subscription } from 'rxjs';
import { IrbSettingsService } from '../irb-settings.service';
import { IGlobalSettings } from '../irb-settings.model';
import { faWindowClose } from '@fortawesome/free-solid-svg-icons';
import { DialogComponent } from '@irb-common/components/dialog/dialog.component';
import { DialogAnnouncementEditComponent } from '@irb/irb-dialogs/dialog-announcement-edit/dialog-announcement-edit.component';
import {IUserDetails} from "@irb/app.model";

@Component({ standalone: false,
  selector: 'irb-settings-dialog',
  templateUrl: './settings-dialog.component.html',
  styleUrls: ['./settings-dialog.component.scss','../../irb-common/styles/dialog.scss'],
  providers: [],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class SettingsDialogComponent implements OnInit, OnDestroy, IEventSubscriber {
  @ViewChild('overlayComponent') private overlay: any;
  @ViewChild("dialog") dialog: DialogComponent;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  _hasUnsavedChanges: boolean = false;
  closeAfterSave: boolean = false;
  eventSubscription: Subscription;
  clickTarget: HTMLElement;
  index: number;
  faWindowClose = faWindowClose;
  isShowAutoTrading = false;
  userDetails: IUserDetails = {} as IUserDetails;
  private pendingFunction: Function;
  onFirstLoad: Function;
  isUiTest = false;

  constructor(public dialogRef: MatDialogRef<SettingsDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public dialogData: any,
              public ps: PageService,
              private as: AppService,
              private cdr: ChangeDetectorRef,
              public iss: IrbSettingsService,
              private router: Router) {
      this.eventSubscription = this.as.getEvents().pipe(takeUntil(this.destroyed$)).subscribe((s) => { this.processEvent(s); });
      this.index = this.dialogData?.defaultTabIndex || this.as.getFromLocal("LAST_TAB_INDEX") || 0;
      this.onFirstLoad = this.dialogData?.onFirstLoad;
      this.iss.setDialogData(this.dialogData);
  }

  ngOnInit() { 
    this.ps.getUserDetails().pipe(takeUntil(this.destroyed$)).subscribe(() => {});
    this.userDetails = this.as.getItem("IRB_APP_CONFIG") as IUserDetails;
    this.isShowAutoTrading = this.userDetails?.isShowAutoTrading;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onTabChange(event: any) {
    this.as.setToLocal("LAST_TAB_INDEX",event.index);
  }

  close() {
    if(this.hasUnsavedChanges) {
      this.clickTarget = null;
      this.closeAfterSave = true;
      this.as.fireEvent(IrbSettingsEvents.UNSAVED_CHANGES);
    } else {
      this.dialogRef.close();
    }
  }

  fireUnsavedChangesEvent(event: MouseEvent) {
    this.hasUnsavedChanges = false;
    this.cdr.detectChanges();
    this.clickTarget = <HTMLElement>document.elementFromPoint(event.x, event.y);
    this.hasUnsavedChanges = true;
    this.cdr.detectChanges();
    this.as.fireEvent(IrbSettingsEvents.UNSAVED_CHANGES);
  }

  processEvent(event: IEvent) {
    switch(event.type) {
      case IrbSettingsEvents.IS_MODIFIED: 
        this.hasUnsavedChanges = true;
        break;
      case IrbSettingsEvents.IS_NOT_MODIFIED: 
        this.hasUnsavedChanges = false;
        break;
      case IrbSettingsEvents.SAVE_SUCCESS:
        this.hasUnsavedChanges = false;
        this.overlay.hideWait();
        this.showMessage(event.data,"success");

        if(this.pendingFunction) {
          this.firePendingFunction();
        } else if(!this.clickTargetFire() && this.closeAfterSave) {
          this.closeAfterSave = false;
          this.close();
        }
        break;
      case IrbSettingsEvents.SAVE_ERROR:
        this.overlay.hideWait();
        this.showMessage("An error occurred processing your request.","error");
        break;
      case IrbSettingsEvents.CLOSE_WITHOUT_SAVE:
        this.hasUnsavedChanges = false;
        this.as.fireEvent(IrbSettingsEvents.REVERT_CHANGES);
        this.cdr.detectChanges();

        if(this.pendingFunction) {
          this.firePendingFunction();
        } else if(!this.clickTargetFire()) {
          this.dialogRef.close();
        }
        break;
      case IrbSettingsEvents.SAVE_START: 
        this.overlay.showWait();
        break;
      case IrbAppEvents.HIDE_WAIT:
        this.overlay.hideWait();
        break;
        case IrbAppEvents.SHOW_CONFIRMATION:
        this.overlay.hideWait();
        break;
      case IrbSettingsEvents.SHOW_ANNOUNCEMENT_EDIT:
        this.showAnnouncementEditDialog(event.data);
        break;
      case IrbSettingsEvents.SHOW_TAB:
        this.openTab(event.data.tabIndex || 0);
        break;
      case IrbSettingsEvents.NAVIGATE:
        this.navigate(event.data.url);
        break;
    }
  }

  openTab(tabIndex: number) {
    if(this.hasUnsavedChanges) {
      this.pendingFunction = () => { this.index = tabIndex || 0; }
      this.as.fireEvent(IrbSettingsEvents.UNSAVED_CHANGES);
    } else {
      this.index = tabIndex || 0;
    }
  }

  navigate(url: string) {
    if(this.hasUnsavedChanges) {
      this.pendingFunction = () => { 
        this.router.navigate([url]); 
        this.pendingFunction = null;
        this.as.fireEvent(IrbSettingsEvents.CLOSE_WITHOUT_SAVE);
      }
      this.as.fireEvent(IrbSettingsEvents.UNSAVED_CHANGES);
    } else {
      this.router.navigate([url]);
      this.as.fireEvent(IrbSettingsEvents.CLOSE_WITHOUT_SAVE);
    }
  }

  firePendingFunction() {
    this.pendingFunction();
    this.pendingFunction = null;
  }

  clickTargetFire() {
    if(this.clickTarget) {
      this.clickTarget.click();
      this.clickTarget = null;
      return true;
    } else {
      return false;
    }
  }

  save() {
    this.as.fireEvent(IrbSettingsEvents.SAVE);    
  }

  showMessage(msg: string, severity: string) {
    this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
      severity: severity,
      summary: "Save",
      detail: msg
    });
  }

  public get hasUnsavedChanges(): boolean {
    return this._hasUnsavedChanges;
  }
  
  public set hasUnsavedChanges(value: boolean) {
    this._hasUnsavedChanges = value;
  }

  @HostListener("window:beforeunload", ["$event"])
  onBeforeUnload(event: any) {
    if (this.hasUnsavedChanges) {      
      this.clickTarget = null;
      this.closeAfterSave = true;
      this.as.fireEvent(IrbSettingsEvents.UNSAVED_CHANGES);
      event.returnValue = false;
    }
  }

  showAnnouncementEditDialog(data: any) {
    const config = this.dialog.getDefaultConfig();
    config.minHeight = "100px";
    config.height = "auto";
    config.width = "650px";
    config.data = data;
    this.dialog.open(DialogAnnouncementEditComponent, config);
  }
}
